import { useState, useRef, useEffect } from 'react';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import { useSavedPresets, useCreatePreset, useDeletePreset } from '@/hooks/useDataVault';
import type { DataExplorerFilters, ExportViewState } from '@/api/dataVault';

interface Props {
  currentFilters: DataExplorerFilters;
  viewState?: ExportViewState;
  onLoadPreset: (filters: DataExplorerFilters, viewState?: ExportViewState) => void;
}

export function SavedFilters({ currentFilters, viewState, onLoadPreset }: Props) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: presets = [] } = useSavedPresets();
  const createMutation = useCreatePreset();
  const deleteMutation = useDeletePreset();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSaving(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSave = async () => {
    if (!presetName.trim()) return;
    const payload = viewState
      ? { ...currentFilters, _view: viewState }
      : currentFilters;
    await createMutation.mutateAsync({
      name: presetName.trim(),
      filters_json: payload as any,
      is_shared: isShared,
    });
    setPresetName('');
    setSaving(false);
    setIsShared(false);
  };

  const handleLoad = (filtersJson: any) => {
    const { _view, ...filters } = filtersJson;
    onLoadPreset(filters as DataExplorerFilters, _view as ExportViewState | undefined);
    setOpen(false);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 text-[10px] tracking-wider text-mono-500 hover:text-mono-300 hover:bg-mono-800 rounded transition-colors"
      >
        <BookmarkIcon className="h-3 w-3" />
        PRESETS
        {presets.length > 0 && (
          <span className="text-mono-600">({presets.length})</span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 w-64 bg-mono-900 border border-mono-700 rounded shadow-xl">
          {/* Preset list */}
          {presets.length > 0 && (
            <div className="max-h-48 overflow-y-auto border-b border-mono-800">
              {presets.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-mono-800 group"
                >
                  <button
                    onClick={() => handleLoad(p.filters_json)}
                    className="flex-1 text-left text-[11px] text-mono-300 hover:text-mono-100 truncate"
                  >
                    {p.name}
                    {p.is_shared && (
                      <span className="ml-1 text-[9px] text-mono-600">(shared)</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="text-[10px] text-mono-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                  >
                    DEL
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Save new */}
          {!saving ? (
            <button
              onClick={() => setSaving(true)}
              className="w-full px-3 py-2 text-left text-[11px] text-mono-400 hover:bg-mono-800 hover:text-mono-200 tracking-wider transition-colors"
            >
              + SAVE CURRENT VIEW
            </button>
          ) : (
            <div className="p-3 space-y-2">
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Preset name..."
                className="w-full px-2 py-1.5 bg-mono-800 border border-mono-700 text-mono-200 text-[11px] rounded focus:outline-none focus:border-mono-500"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-[10px] text-mono-500">
                  <input
                    type="checkbox"
                    checked={isShared}
                    onChange={(e) => setIsShared(e.target.checked)}
                    className="rounded bg-mono-800 border-mono-700"
                  />
                  SHARED
                </label>
                <div className="flex-1" />
                <button
                  onClick={() => setSaving(false)}
                  className="px-2 py-1 text-[10px] text-mono-500 hover:text-mono-300 transition-colors"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleSave}
                  disabled={!presetName.trim() || createMutation.isPending}
                  className="px-2 py-1 text-[10px] tracking-wider bg-mono-700 text-mono-200 rounded hover:bg-mono-600 disabled:opacity-50 transition-colors"
                >
                  SAVE
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
